import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Download, Printer } from 'lucide-react';
import { Button, PageHeader, Skeleton, StatusBadge } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/axios';
import { type TiersType, TIERS_TYPE_LABEL, getTier } from '@/services/tiers';
import { listDepenses, openEtatDepensesPdf } from '@/services/depenses';
import { CATEGORIE_LABEL } from '@/modules/depenses/labels';
import { toast } from '@/store/toastStore';
import { formatDZD } from '@/utils/algeria';

const TYPE_BADGE: Record<TiersType, 'info' | 'working' | 'review' | 'done'> = {
  CLIENT: 'info', FOURNISSEUR: 'working', SOUS_TRAITANT: 'review', LES_TROIS: 'done',
};
const TYPE_LISTE: Record<TiersType, { label: string; to: string }> = {
  CLIENT: { label: 'Clients', to: '/clients' },
  FOURNISSEUR: { label: 'Fournisseurs', to: '/fournisseurs' },
  SOUS_TRAITANT: { label: 'Sous-traitants', to: '/sous-traitants' },
  LES_TROIS: { label: 'Clients', to: '/clients' },
};

export function TiersDetailPage() {
  const { id = '' } = useParams();
  const { data: tiers, isLoading } = useQuery({ queryKey: ['tiers', id], queryFn: () => getTier(id), enabled: !!id });
  const { data: depenses } = useQuery({
    queryKey: ['depenses', 'tiers', id],
    queryFn: () => listDepenses({ tiers_id: id, size: 200 }),
    enabled: !!id,
  });

  const totaux = useMemo(() => {
    const items = depenses?.items ?? [];
    return items.reduce(
      (acc, d) => {
        acc.ht += Number(d.montant_ht);
        acc.tva += Number(d.tva_montant);
        acc.ttc += Number(d.montant_ttc);
        if (d.tva_deductible) acc.tvaDed += Number(d.tva_montant);
        return acc;
      },
      { ht: 0, tva: 0, ttc: 0, tvaDed: 0 },
    );
  }, [depenses]);

  if (isLoading || !tiers) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const imprimer = async (mode: 'open' | 'download' = 'open') => {
    try {
      await openEtatDepensesPdf({ tiers_id: id }, mode);
    } catch (e) {
      toast.error(getApiErrorMessage(e));
    }
  };

  const infos: [string, string | null][] = [
    ...(tiers.type !== 'CLIENT' ? [['Spécialité', tiers.specialite] as [string, string | null]] : []),
    ['NIF', tiers.nif],
    ['RC (Registre du Commerce)', tiers.rc],
    ["AI (Article d'Imposition)", tiers.ai],
    ['Téléphone', tiers.telephone],
    ['Email', tiers.email],
    ['Adresse', tiers.adresse],
    ['Contact', tiers.contact_nom],
    ['Contact (téléphone)', tiers.contact_telephone],
  ];
  const rib = [tiers.rib_banque, tiers.rib_agence, tiers.rib_compte, tiers.rib_cle].filter(Boolean).join(' ');

  return (
    <>
      <PageHeader
        title={tiers.raison_sociale}
        subtitle={`${tiers.code} · ${TIERS_TYPE_LABEL[tiers.type]}`}
        breadcrumbs={[{ label: 'Advanced Works ERP' }, { label: TYPE_LISTE[tiers.type].label, to: TYPE_LISTE[tiers.type].to }, { label: tiers.code }]}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" onClick={() => imprimer('open')}>
              <Printer /> Imprimer l'état
            </Button>
            <Button variant="secondary" onClick={() => imprimer('download')}>
              <Download /> Télécharger
            </Button>
          </div>
        }
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-base font-bold text-content-primary">Informations</h2>
            <StatusBadge status={TYPE_BADGE[tiers.type]} label={TIERS_TYPE_LABEL[tiers.type]} />
          </div>
          <dl className="grid grid-cols-2 gap-4">
            {infos.map(([k, v]) => (
              <div key={k}>
                <dt className="label-caps">{k}</dt>
                <dd className="mt-0.5 text-sm font-medium text-content-primary">{v || '—'}</dd>
              </div>
            ))}
            <div className="col-span-2">
              <dt className="label-caps">RIB</dt>
              <dd className="mt-0.5 text-sm font-medium tabular-nums text-content-primary">{rib || '—'}</dd>
            </div>
          </dl>
          {tiers.notes && <p className="mt-4 text-sm text-content-secondary">{tiers.notes}</p>}
        </div>

        <div className="rounded-lg border border-border bg-surface-secondary p-5 shadow-sm">
          <h2 className="mb-3 text-base font-bold text-content-primary">Synthèse des dépenses</h2>
          <dl className="space-y-2 text-sm">
            <div className="flex justify-between"><dt className="text-content-secondary">Nombre</dt><dd className="font-semibold tabular-nums">{depenses?.items.length ?? 0}</dd></div>
            <div className="flex justify-between"><dt className="text-content-secondary">Total HT</dt><dd className="font-semibold tabular-nums">{formatDZD(totaux.ht)}</dd></div>
            <div className="flex justify-between"><dt className="text-content-secondary">Total TVA</dt><dd className="font-semibold tabular-nums">{formatDZD(totaux.tva)}</dd></div>
            <div className="flex justify-between"><dt className="text-content-secondary">dont déductible</dt><dd className="font-semibold tabular-nums text-status-done">{formatDZD(totaux.tvaDed)}</dd></div>
            <div className="flex justify-between border-t border-border pt-2"><dt className="font-bold text-content-primary">Total TTC</dt><dd className="font-bold tabular-nums text-brand-primary">{formatDZD(totaux.ttc)}</dd></div>
          </dl>
        </div>
      </div>

      <div className="mt-4 overflow-hidden rounded-lg border border-border bg-surface-secondary shadow-sm">
        <div className="border-b border-border px-5 py-3">
          <h2 className="text-base font-bold text-content-primary">Dépenses liées</h2>
        </div>
        {depenses && depenses.items.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="bg-surface-primary">
              <tr className="border-b border-border text-left">
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Date</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Référence</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">N° facture</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Libellé</th>
                <th className="px-4 py-2.5 font-semibold text-content-secondary">Catégorie</th>
                <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">HT</th>
                <th className="px-4 py-2.5 text-right font-semibold text-content-secondary">TTC</th>
              </tr>
            </thead>
            <tbody>
              {depenses.items.map((d) => (
                <tr key={d.id} className="border-b border-border last:border-0 hover:bg-surface-hover">
                  <td className="px-4 py-2.5 tabular-nums text-content-secondary">{d.date_depense}</td>
                  <td className="px-4 py-2.5 text-content-secondary">{d.reference}</td>
                  <td className="px-4 py-2.5 text-content-secondary">{d.numero_facture ?? '—'}</td>
                  <td className="px-4 py-2.5 font-medium text-content-primary">{d.libelle}</td>
                  <td className="px-4 py-2.5 text-xs text-content-secondary">{CATEGORIE_LABEL[d.categorie]}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatDZD(d.montant_ht)}</td>
                  <td className="px-4 py-2.5 text-right font-medium tabular-nums">{formatDZD(d.montant_ttc)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="px-5 py-10 text-center text-sm text-content-muted">Aucune dépense liée à ce tiers.</p>
        )}
      </div>
    </>
  );
}
